import type {
  ClientSafeGovernanceViewV1,
  ClientSafeHandoffViewV1,
  ClientSafeReportViewV1,
  ComposeLaunchProofInput,
} from "@enrein/shared-contracts";
import { clientSafeProofReferenceLabel } from "@enrein/shared-contracts/reports/client-safe-proof-reference-label";
import { toHandoffIssuanceLabel } from "@enrein/shared-contracts/display/tier1-client-display";
import type { ClientPortalApiClient } from "./api.js";

export interface PortalLaunchProofApiSnapshot {
  readonly report: ClientSafeReportViewV1 | null;
  readonly governance: ClientSafeGovernanceViewV1 | null;
  readonly handoff: ClientSafeHandoffViewV1 | null;
}

export async function loadPortalLaunchProofApiSnapshot(
  api: ClientPortalApiClient,
  clientLinkId: string,
  token: string,
): Promise<PortalLaunchProofApiSnapshot> {
  const [report, governance, handoff] = await Promise.all([
    api.getCurrentClientReport(clientLinkId, token).catch(() => null),
    api.getGovernance(clientLinkId, token).catch(() => null),
    api.getHandoff(clientLinkId, token).catch(() => null),
  ]);

  return { report, governance, handoff };
}

export function buildLaunchProofInputFromPortalApis(
  snapshot: PortalLaunchProofApiSnapshot,
  generatedAt: string = new Date().toISOString(),
): ComposeLaunchProofInput {
  const { report, governance, handoff } = snapshot;

  const departureKitSummary = handoff
    ? `Handoff package: ${toHandoffIssuanceLabel(handoff.issuanceStatus)}.`
    : governance?.coverageSummary;

  return {
    phase1Results: undefined,
    proofReferenceLabel: report
      ? clientSafeProofReferenceLabel({
          rawLabel: report.publicReportRef,
          headline: report.headline,
          reportTypeLabel: report.reportTypeLabel,
        })
      : "Launch proof summary",
    generatedAt,
    searchReadinessHeadline: report?.headline,
    searchReadinessQaStatus: undefined,
    searchReadinessCoverage: report?.coverage,
    searchReadinessStaleBanner: report?.staleBanner ?? null,
    departureKitSummary,
    managerExceptions: governance?.managerExceptionSummary
      ? [governance.managerExceptionSummary]
      : undefined,
    clientMissingInfo: governance?.responsibilityBullets,
  };
}
